import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CreateCreditPolicyPayload,
  CreditPolicyEvaluationRecord,
  EvaluateCreditPayload,
  SalesApiService,
  SalesChannelRecord,
  UpdateCreditPolicyPayload
} from './sales-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-credit-policy-detail',
  imports: [
    AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, InputTextModule,
    InputNumberModule, SelectModule, TableModule, CustomerSelectorComponent, CurrencySelectorComponent
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('sales.credit.policies.form.createTitle') : t('sales.credit.policies.form.editTitle')" [description]="t('sales.credit.policies.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <header class="card header">
          <div class="toolbar">
            <a class="back-link" [routerLink]="['/sales/credit-policies']">{{ t('catalog.action.backToList') }}</a>
            <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
          </div>
          <div class="summary">
            <span>{{ t('sales.credit.fields.code') }}: <strong>{{ form.code || '-' }}</strong></span>
            <span>{{ t('sales.credit.fields.scopeType') }}: <strong>{{ scopeLabel(form.scopeType) }}</strong></span>
          </div>
        </header>

        @if (isCreateMode()) {
          <section class="card grid">
            <label><span>{{ t('sales.credit.fields.code') }}</span><input pInputText [(ngModel)]="form.code" /></label>
            <label><span>{{ t('sales.credit.fields.name') }}</span><input pInputText [(ngModel)]="form.name" /></label>
            <label>
              <span>{{ t('sales.credit.fields.scopeType') }}</span>
              <p-select [options]="scopeOptions" optionValue="value" [(ngModel)]="form.scopeType" appendTo="body" (ngModelChange)="form.scopeId = ''">
                <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
              </p-select>
            </label>
            @if (form.scopeType === 'Channel') {
              <label><span>{{ t('sales.credit.fields.scopeTarget') }}</span><p-select [options]="channelOptions()" optionLabel="label" optionValue="value" [(ngModel)]="form.scopeId" appendTo="body" /></label>
            } @else if (form.scopeType === 'Customer') {
              <label><span>{{ t('sales.credit.fields.scopeTarget') }}</span><ain-customer-selector [(ngModel)]="form.scopeId" /></label>
            }
            <label><span>{{ t('sales.credit.fields.defaultLimit') }}</span><p-inputNumber [(ngModel)]="form.defaultCreditLimit" [min]="0" /></label>
            <label><span>{{ t('sales.credit.fields.maxRiskScore') }}</span><p-inputNumber [(ngModel)]="form.maxRiskScore" [min]="0" [max]="1000" /></label>
            <label><span>{{ t('sales.credit.fields.priority') }}</span><p-inputNumber [(ngModel)]="form.priority" [min]="0" /></label>
            <label class="check"><input type="checkbox" [(ngModel)]="form.blockWhenOverdue" /><span>{{ t('sales.credit.fields.blockWhenOverdue') }}</span></label>
          </section>
        } @else {
          <div class="tabs">
            <button type="button" [class.active]="activeTab() === 'general'" (click)="activeTab.set('general')">{{ t('sales.credit.tabs.general') }}</button>
            <button type="button" [class.active]="activeTab() === 'evaluate'" (click)="activeTab.set('evaluate')">{{ t('sales.credit.tabs.evaluate') }}</button>
          </div>
          @if (activeTab() === 'general') {
            <section class="card grid">
              <label><span>{{ t('sales.credit.fields.code') }}</span><input pInputText [(ngModel)]="form.code" readonly /></label>
              <label><span>{{ t('sales.credit.fields.name') }}</span><input pInputText [(ngModel)]="form.name" /></label>
              <label><span>{{ t('sales.credit.fields.defaultLimit') }}</span><p-inputNumber [(ngModel)]="form.defaultCreditLimit" [min]="0" /></label>
              <label><span>{{ t('sales.credit.fields.maxRiskScore') }}</span><p-inputNumber [(ngModel)]="form.maxRiskScore" [min]="0" [max]="1000" /></label>
              <label><span>{{ t('sales.credit.fields.priority') }}</span><p-inputNumber [(ngModel)]="form.priority" [min]="0" /></label>
              <label class="check"><input type="checkbox" [(ngModel)]="form.blockWhenOverdue" /><span>{{ t('sales.credit.fields.blockWhenOverdue') }}</span></label>
              <label class="check"><input type="checkbox" [(ngModel)]="form.isActive" /><span>{{ t('sales.credit.active') }}</span></label>
            </section>
          }
          @if (activeTab() === 'evaluate') {
            <section class="card grid">
              <label><span>{{ t('sales.orders.fields.customer') }}</span><ain-customer-selector [(ngModel)]="evaluateForm.customerAccountId" /></label>
              <label><span>{{ t('sales.orders.fields.channel') }}</span><p-select [options]="channelOptions()" optionLabel="label" optionValue="value" [(ngModel)]="evaluateForm.salesChannelId" [showClear]="true" appendTo="body" /></label>
              <label><span>{{ t('sales.credit.fields.currency') }}</span><ain-currency-selector [(ngModel)]="evaluateForm.currencyCode" /></label>
              <label><span>{{ t('sales.credit.evaluate.proposedAmount') }}</span><p-inputNumber [(ngModel)]="evaluateForm.proposedAmount" [min]="0" /></label>
              <div class="evaluate-action"><button pButton type="button" [label]="t('sales.credit.evaluate.run')" [disabled]="evaluating()" (click)="runEvaluation()"></button></div>
            </section>
            @if (evaluationResult()) {
              <section class="card">
                <p class="result" [class.denied]="!evaluationResult()!.allowed">{{ evaluationResult()!.allowed ? t('sales.credit.evaluate.allowed') : t('sales.credit.evaluate.denied') }}</p>
                <div class="metrics">
                  <div><span>{{ t('sales.credit.evaluate.effectiveLimit') }}</span><strong>{{ evaluationResult()!.effectiveLimit }}</strong></div>
                  <div><span>{{ t('sales.credit.evaluate.exposure') }}</span><strong>{{ evaluationResult()!.currentExposure }}</strong></div>
                  <div><span>{{ t('sales.credit.evaluate.available') }}</span><strong>{{ evaluationResult()!.availableCredit }}</strong></div>
                </div>
                @if (evaluationResult()!.violations.length) {
                  <ul class="violations">@for (violation of evaluationResult()!.violations; track violation) { <li>{{ violation }}</li> }</ul>
                }
                <p-table [value]="evaluationResult()!.policies" [paginator]="true" [rows]="10">
                  <ng-template pTemplate="header">
                    <tr><th>{{ t('sales.credit.fields.code') }}</th><th>{{ t('sales.credit.fields.defaultLimit') }}</th><th>{{ t('columns.status') }}</th></tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-row>
                    <tr><td>{{ row.code }}</td><td>{{ row.defaultCreditLimit ?? '-' }}</td><td>{{ row.allowed ? t('sales.credit.evaluate.allowed') : t('sales.credit.evaluate.denied') }}</td></tr>
                  </ng-template>
                </p-table>
              </section>
            }
          }
        }
        @if (errorMessage()) { <p class="error">{{ errorMessage() }}</p> }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { display: flex; justify-content: space-between; align-items: center; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; margin-bottom: 10px; }
    .header .summary { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 10px; font-size: 13px; color: #475569; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    label.check { grid-template-columns: auto 1fr; align-items: center; }
    .evaluate-action { display: flex; align-items: end; }
    .tabs { display: flex; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
    .tabs button { border: 1px solid #cbd5e1; background: #fff; border-radius: 999px; padding: 6px 12px; cursor: pointer; }
    .tabs button.active { background: #1d4ed8; border-color: #1d4ed8; color: #fff; }
    .result { font-weight: 600; margin: 0 0 8px; color: #047857; }
    .result.denied { color: #b91c1c; }
    .metrics { display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 10px; font-size: 13px; }
    .violations { margin: 0 0 10px; padding-inline-start: 18px; color: #b91c1c; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) { .grid { grid-template-columns: 1fr; } }
  `]
})
export class SalesCreditPolicyDetailComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly evaluating = signal(false);
  readonly isCreateMode = signal(false);
  readonly errorMessage = signal('');
  readonly activeTab = signal<'general' | 'evaluate'>('general');
  readonly channels = signal<SalesChannelRecord[]>([]);
  readonly evaluationResult = signal<{
    allowed: boolean;
    violations: string[];
    effectiveLimit: number;
    currentExposure: number;
    availableCredit: number;
    policies: CreditPolicyEvaluationRecord[];
    decisionId: string;
  } | null>(null);

  readonly scopeOptions = [
    { value: 'Global', labelKey: 'sales.credit.scopes.global' },
    { value: 'Channel', labelKey: 'sales.credit.scopes.channel' },
    { value: 'Customer', labelKey: 'sales.credit.scopes.customer' }
  ];

  form = {
    code: '',
    name: '',
    scopeType: 'Global',
    scopeId: '' as string | null,
    defaultCreditLimit: null as number | null,
    blockWhenOverdue: true,
    maxRiskScore: null as number | null,
    priority: 100,
    isActive: true
  };

  evaluateForm = {
    customerAccountId: '' as string | null,
    salesChannelId: '' as string | null,
    currencyCode: 'IRR',
    proposedAmount: 0
  };

  ngOnInit(): void {
    this.api.listChannels().subscribe({ next: rows => this.channels.set(rows), error: () => this.channels.set([]) });
    const policyId = this.route.snapshot.paramMap.get('policyId');
    this.isCreateMode.set(!policyId || policyId === 'new');
    if (this.isCreateMode()) { this.loading.set(false); return; }
    this.loadPolicy(policyId!);
  }

  channelOptions(): { label: string; value: string }[] {
    return this.channels().map(item => ({ label: `${item.code} - ${item.name}`, value: item.id }));
  }

  loadPolicy(id: string): void {
    this.api.getCreditPolicy(id).subscribe({
      next: policy => {
        this.form = {
          code: policy.code,
          name: policy.name,
          scopeType: policy.scopeType,
          scopeId: policy.scopeId ?? null,
          defaultCreditLimit: policy.defaultCreditLimit ?? null,
          blockWhenOverdue: policy.blockWhenOverdue,
          maxRiskScore: policy.maxRiskScore ?? null,
          priority: policy.priority,
          isActive: policy.isActive
        };
        this.loading.set(false);
      },
      error: () => { this.errorMessage.set(this.t('sales.credit.policies.errorLoad')); this.loading.set(false); }
    });
  }

  save(): void {
    this.saving.set(true);
    this.errorMessage.set('');
    if (this.isCreateMode()) {
      const payload: CreateCreditPolicyPayload = {
        code: this.form.code,
        name: this.form.name,
        scopeType: this.form.scopeType,
        scopeId: this.form.scopeType === 'Global' ? null : (this.form.scopeId || null),
        defaultCreditLimit: this.form.defaultCreditLimit,
        blockWhenOverdue: this.form.blockWhenOverdue,
        maxRiskScore: this.form.maxRiskScore,
        priority: this.form.priority
      };
      this.api.createCreditPolicy(payload).subscribe({
        next: policy => void this.router.navigate(['/sales/credit-policies', policy.id]),
        error: () => { this.errorMessage.set(this.t('sales.credit.policies.errorSave')); this.saving.set(false); }
      });
      return;
    }
    const id = this.route.snapshot.paramMap.get('policyId')!;
    const payload: UpdateCreditPolicyPayload = {
      name: this.form.name,
      defaultCreditLimit: this.form.defaultCreditLimit,
      blockWhenOverdue: this.form.blockWhenOverdue,
      maxRiskScore: this.form.maxRiskScore,
      priority: this.form.priority,
      isActive: this.form.isActive
    };
    this.api.updateCreditPolicy(id, payload).subscribe({
      next: () => this.saving.set(false),
      error: () => { this.errorMessage.set(this.t('sales.credit.policies.errorSave')); this.saving.set(false); }
    });
  }

  runEvaluation(): void {
    if (!this.evaluateForm.customerAccountId) {
      this.errorMessage.set(this.t('sales.credit.evaluate.customerRequired'));
      return;
    }
    this.evaluating.set(true);
    this.errorMessage.set('');
    const payload: EvaluateCreditPayload = {
      customerAccountId: this.evaluateForm.customerAccountId,
      salesChannelId: this.evaluateForm.salesChannelId || null,
      currencyCode: this.evaluateForm.currencyCode,
      proposedAmount: this.evaluateForm.proposedAmount,
      correlationKey: `eval-${Date.now()}`
    };
    this.api.evaluateCredit(payload).subscribe({
      next: result => { this.evaluationResult.set(result); this.evaluating.set(false); },
      error: () => { this.errorMessage.set(this.t('sales.credit.evaluate.error')); this.evaluating.set(false); }
    });
  }

  scopeLabel(scopeType: string): string {
    return this.localization.translate(`sales.credit.scopes.${scopeType.charAt(0).toLowerCase()}${scopeType.slice(1)}`);
  }

  t(key: string): string { return this.localization.translate(key); }
}
